import { supabase } from './supabase';

interface ApiOptions {
  body?: Record<string, unknown>;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
}

interface ApiResponse<T = unknown> {
  data: T | null;
  error: string | null;
}

/**
 * Invoke a Supabase Edge Function.
 * Automatically includes auth headers from the current session.
 */
export async function invokeFunction<T = unknown>(
  functionName: string,
  options: ApiOptions = {}
): Promise<ApiResponse<T>> {
  const { body, method = 'POST' } = options;

  const { data, error } = await supabase.functions.invoke(functionName, {
    body: body ? JSON.stringify(body) : undefined,
    method,
  });

  if (error) {
    return { data: null, error: error.message };
  }

  return { data: data as T, error: null };
}

/**
 * Call api-access-control with an action.
 */
export async function accessControlAction<T = unknown>(
  action: string,
  params: Record<string, unknown> = {}
): Promise<ApiResponse<T>> {
  return invokeFunction<T>('api-access-control', {
    body: { action, ...params },
  });
}

/**
 * Call api-access-control without authentication (for public intercom actions).
 * Uses the Supabase anon key for function invocation.
 */
export async function intercomAction<T = unknown>(
  action: string,
  params: Record<string, unknown> = {}
): Promise<ApiResponse<T>> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  const url = `${supabaseUrl}/functions/v1/api-access-control`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: anonKey,
      },
      body: JSON.stringify({ action, ...params }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { data: null, error: data?.error || `Fel: ${response.status}` };
    }

    return { data: data as T, error: null };
  } catch (e: any) {
    return { data: null, error: e.message || 'Nätverksfel' };
  }
}

/**
 * Call api-access endpoints.
 */
export async function accessApi<T = unknown>(
  path: string,
  options: ApiOptions = {}
): Promise<ApiResponse<T>> {
  const { body, method = 'GET' } = options;

  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;

  if (!token) {
    return { data: null, error: 'Ej autentiserad' };
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const url = `${supabaseUrl}/functions/v1/api-access${path}`;

  const response = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    return {
      data: null,
      error: errorData?.error || `Fel: ${response.status}`,
    };
  }

  const data = await response.json();
  return { data: data as T, error: null };
}
